import { Component, OnInit, Output, Input, ViewChild, ElementRef } from '@angular/core';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { Orgstyle } from './model/orgstyle';
import { OrgstyleService } from './service/orgstyle.service';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-orgstyle',
  templateUrl: './orgstyle.component.html',
  styleUrls: ['./orgstyle.component.css']
})
export class OrgstyleComponent implements OnInit {
  @Output() orgstyle: Orgstyle;
  @Input() admin: boolean;
  resettoggler: boolean;

  // ===== Import/Export state =====
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;
  importPreview: Partial<Orgstyle> | null = null;
  applyMode: 'merge' | 'replace' = 'merge';

  // optional: copy directly from another org
  sourceOrgId: string;
  loadingCopy = false;

  // color keys for minimal validation
  private readonly colorLikeKeys = new Set<string>([
    'mainColor','negMainColor','topnavmenuiconcolor','topnavmenuiconcolorhover',
    'topnavmenuiconbg','topnavmenuiconbghover','topnavnmenuborderbottom',
    'footernaviconbg','footernaviconbghover','footernaviconcolor','footernaviconcolorhover',
    'pagetitlecolor','pagetitlesubcolor','portaliconcolor','portaliconcolorhover',
    'portaliconbg','portaliconbghover','orgtitlecolor','orgFontColor',
    'orgiconColor','orgiconhoverColor','buttontextColor','buttonbgcolor',
    'buttonbghovercolor','buttontextHoverColor','tabletextcolor',
    'customerpagelabelcolor','dropdowntextcolor','taskmytaskbg',
    'taskvendorbg','taskcustomerbg','taskdonebg','tasktextcolor',
    'notificationbgcolor','notificationtitlecolor','notificationdescriptioncolor'
  ]);

  // whitelist of fields we allow to be imported/exported
private readonly ORG_STYLE_KEYS: (keyof Orgstyle | '_version')[] = [
  'organizationId',
  'whitelabelId',
  'mainColor',
  'negMainColor',
  'borderRad',
  'orgFont',

  'taskcustomerbg',
  'taskvendorbg',
  'taskmytaskbg',
  'taskdonebg',
  'tasktextcolor',

  'notificationbgcolor',
  'notificationtitlecolor',
  'notificationdescriptioncolor',

  'topnavnmenuweight',
  'topnavmenuweightactive',
  'topnavmenufont',
  'topnavmenusize',
  'topnavmenuiconcolor',
  'topnavmenuiconbg',
  'topnavmenuiconcolorhover',
  'topnavmenuiconbghover',
  'topnavnmenuborderbottom',

  'pagetitlefont',
  'pagetitlesize',
  'pagetitleweight',
  'pagetitlecolor',
  'pagetitlesubfont',
  'pagetitlesubsize',
  'pagetitlesubweight',
  'pagetitlesubcolor',

  'portaliconcolor',
  'portaliconcolorhover',
  'portaliconbg',
  'portaliconbghover',

  'taskanimation',

  'footernaviconcolor',
  'footernaviconcolorhover',
  'footernaviconbg',
  'footernaviconbghover',

  'orgContentFont',
  'orgFontColor',
  'buttontextColor',
  'orgBrandWeight',
  'brandControl',
  'logoformat',
  'sidenav',
  'orgfavicon',
  'buttonbgcolor',
  'buttonbghovercolor',
  'orgtitlecolor',
  'navborder',
  'orgiconColor',
  'buttontextHoverColor',
  'orgiconhoverColor',
  'dropdowntextcolor',
  'tabletextcolor',
  'plannerdaycolor',
  'customerpagelabelcolor',
  'infopinhoverbgcolor',
  'infopinbgcolor',
  'footertextcolor',
  'emailmarkup',

  '_version'
];


  constructor(private ossrv: OrgstyleService) {}

  ngOnInit() {
    this.orgstyle = new Orgstyle();
    this.orgstyle.sidenav = 'top';
    this.resettoggler = false;

    if (SessionHelper.getDynamicStyle() && SessionHelper.getDynamicStyle() !== 'undefined') {
      this.orgstyle = JSON.parse(SessionHelper.getDynamicStyle());
    } else {
      this.ossrv.getOrgstyles().pipe(untilDestroyed(this)).subscribe(style => {
        if (style[0]) {
          this.orgstyle = style[0];
        }
      });
    }
  }

  createOrgStyle() {
    this.ossrv.createOrgstyle(this.orgstyle).pipe(untilDestroyed(this)).subscribe(id => {
      this.orgstyle.whitelabelId = id;
      window.location.reload();
    });
  }

  resetStyles() {
    this.ossrv.getDefaultOrgStyle().pipe(untilDestroyed(this)).subscribe(dstyle => {
      this.orgstyle = dstyle;
      this.updateOrgStyle();
    });
  }

  updateOrgStyle() {
    this.orgstyle.whitelabelId = 'x';
    this.orgstyle.brandControl = 'none';
    this.ossrv.updateOrgstyle(this.orgstyle).pipe(untilDestroyed(this)).subscribe(() => {
      SessionHelper.setDynamicStyle(JSON.stringify(this.orgstyle));
      window.location.reload();
    });
  }

  deleteOrgStyle() {
    this.ossrv.deleteOrgstyle(this.orgstyle).pipe(untilDestroyed(this)).subscribe(() => {
      window.location.reload();
    });
  }

  togglereset() {
    this.resettoggler = !this.resettoggler;
  }

  // ===== Export current style to file =====
  exportOrgStyle() {
    const payload = this.buildExportPayload(this.orgstyle);
    const orgIdForName = (this.orgstyle as any)?.organizationId || 'org';
    const filename = `orgstyle_${orgIdForName}_${new Date().toISOString().slice(0,10)}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ===== Trigger hidden file input (import) =====
  triggerImport() {
    if (!this.fileInput) return;
    this.fileInput.nativeElement.value = ''; // allow re-select same file
    this.fileInput.nativeElement.click();
  }

  // ===== Handle selected file and show preview =====
  async importOrgStyleFile(evt: Event) {
    try {
      const input = evt.target as HTMLInputElement;
      if (!input.files?.length) return;
      const file = input.files[0];
      const text = await file.text();
      const parsed = JSON.parse(text);
      const sanitized = this.sanitizeImportedStyle(parsed);
      this.importPreview = sanitized;
      // show Bootstrap modal (uses your existing Bootstrap JS)
      (window as any).$?.('#orgStylePreviewModal')?.modal('show');
    } catch (e) {
      console.error('Invalid import file', e);
      alert('Invalid import file. Please select a valid orgstyle JSON.');
    }
  }

  // ===== Apply imported preview to current state (merge/replace) =====
applyImportedStyle(saveNow = false) {
  if (!this.importPreview) return;

  if (this.applyMode === 'replace') {
    const fresh: any = {};
    this.ORG_STYLE_KEYS.forEach(k => fresh[k as any] = undefined);
    this.orgstyle = { ...(fresh as Orgstyle), ...(this.importPreview as Orgstyle) };
  } else {
    this.orgstyle = { ...this.orgstyle, ...(this.importPreview as Orgstyle) };
  }
  (this.orgstyle as any)._version = (this.orgstyle as any)._version || 1;

  if (saveNow) {
    this.updateOrgStyle(); // persists + reloads (your current behavior)
  }

  // Close only the preview modal
  (window as any).$?.('#orgStylePreviewModal')?.modal('hide');
}


  // ===== Optional: copy from another org via backend =====
  loadFromOrgId() {
    if (!this.sourceOrgId) return;
    this.loadingCopy = true;
    this.ossrv
      .getOrgstyleByOrgId(this.sourceOrgId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (other) => {
          const sanitized = this.sanitizeImportedStyle(other);
          this.importPreview = sanitized;
          this.applyMode = 'replace';
          (window as any).$?.('#orgStylePreviewModal')?.modal('show');
        },
        error: (err) => {
          console.error(err);
          alert('Could not load styles from the specified org.');
        },
        complete: () => (this.loadingCopy = false)
      });
  }

  // ===== Helpers =====
  private sanitizeImportedStyle(input: any): Partial<Orgstyle> {
    const out: Partial<Orgstyle> = {};
    for (const key of this.ORG_STYLE_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
      const val = input[key as string];
      if (val == null || val === '') continue;

      if (this.colorLikeKeys.has(key as string)) {
        // Allow #RGB, #RRGGBB, #RRGGBBAA
        if (typeof val !== 'string' || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(val)) {
          continue; // skip invalid colors
        }
      }
      (out as any)[key] = val;
    }
    if (!(out as any)._version) (out as any)._version = 1;
    return out;
  }

  private buildExportPayload(state: Orgstyle): Partial<Orgstyle> & { _version: number } {
    const out: any = {};
    this.ORG_STYLE_KEYS.forEach(k => {
      const v = (state as any)?.[k];
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    out._version = out._version || 1;
    return out;
  }
}
